import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PLAN_MODULES, planHasModule } from '@/components/PlanModulesList';
import { useTenantModules } from '@/lib/useTenantModules';
import { Lock, ArrowRight, Check, MessageCircle, Sparkles, ArrowLeft, CreditCard } from 'lucide-react';
import { PaymentModal } from '@/components/PaymentModal';
import { fetchTenantModules } from '@/lib/useTenantModules';
import { toast } from 'sonner';

const DETALHES = {
  whatsapp: {
    headline: 'Venda 24h pelo WhatsApp sem contratar ninguém',
    beneficios: ['Bot com IA responde clientes, envia cardápio/catálogo e anota pedidos', 'Pedidos caem direto no sistema e imprimem na cozinha', 'Cobrança automática de contas a receber pelo WhatsApp', 'Confirmação de pedido e entrega sem digitar nada'],
    stat: 'Clientes relatam até 30% mais pedidos fora do horário comercial',
  },
  online: {
    headline: 'Sua loja virtual pronta, com link próprio e pedidos integrados',
    beneficios: ['Catálogo online com sua logo, capa e cor da marca', 'Cliente monta o pedido e finaliza pelo WhatsApp ou no site', 'Pedidos entram no sistema e viram NFC-e em um clique', 'Taxa de entrega, pedido mínimo e horário de funcionamento'],
    stat: 'Sem taxa por pedido, diferente dos aplicativos de delivery',
  },
  marketplace: {
    headline: 'Todos os seus canais de venda em um só lugar',
    beneficios: ['iFood, Rappi, Zé Delivery, 99Food, Shopee, Shein, TikTok Shop e Mercado Livre', 'Pedidos entram automaticamente e imprimem na cozinha', 'Relatório de vendas por canal e por período', 'Emissão de NFC-e/NF-e direto do pedido do marketplace'],
    stat: 'Zero digitação manual de pedidos de aplicativos',
  },
  pdv: {
    headline: 'Frente de caixa rápida com NFC-e em segundos',
    beneficios: ['Leitor de código de barras, balança e impressora térmica', 'Cupom fiscal homologado com contingência offline', 'Sangria, suprimento e fechamento de caixa', 'Múltiplos caixas e usuários'],
    stat: 'Venda registrada e cupom emitido em menos de 3 segundos',
  },
};

export default function ModuleLockedPage({ moduleKey: propKey }) {
  const params = useParams();
  const navigate = useNavigate();
  const key = propKey || params.module;
  const mod = PLAN_MODULES.find((m) => m.key === key) || PLAN_MODULES[0];
  const det = DETALHES[mod.key] || DETALHES.pdv;
  const { planName } = useTenantModules();
  const [plans, setPlans] = useState([]);
  const [payPlan, setPayPlan] = useState(null);
  const [selected, setSelected] = useState(null);

  const onPaid = async () => {
    setPayPlan(null);
    toast.success('Pagamento confirmado! Módulo liberado.');
    await fetchTenantModules(true);
    navigate(mod.key === 'pdv' ? '/pos' : mod.key === 'online' ? '/pedidos-online' : mod.key === 'whatsapp' ? '/whatsapp' : '/marketplaces');
  };

  useEffect(() => {
    api.get('/saas/plans').then((r) => setPlans((r.data.plans || []).filter((p) => planHasModule(p, mod)).sort((a, b) => a.price - b.price))).catch(() => setPlans([]));
  }, [mod]);

  const Icon = mod.icon;
  const melhor = plans.find((p) => p.id === selected) || plans[0];

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-8" data-testid={`module-locked-${mod.key}`}>
      <div className="w-full max-w-5xl grid lg:grid-cols-[1.2fr,1fr] gap-8 items-stretch">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 sm:p-12">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-amber-500/25 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-orange-600/20 rounded-full blur-3xl" />
          <div className="relative">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-8" data-testid="module-locked-back"><ArrowLeft className="w-3.5 h-3.5" /> Voltar</button>
            <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-400 px-3 py-1.5 rounded-full text-xs mb-6">
              <Lock className="w-3.5 h-3.5" /> Módulo não incluído no plano {planName || 'atual'}
            </div>
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30"><Icon className="w-7 h-7 text-white" /></div>
              <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{mod.label}</h1>
            </div>
            <p className="text-lg text-slate-300 mb-8">{det.headline}</p>
            <ul className="space-y-3 mb-8">
              {det.beneficios.map((b) => (
                <li key={b} className="flex items-start gap-3 text-slate-200"><Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />{b}</li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-sm text-amber-300"><Sparkles className="w-4 h-4" />{det.stat}</div>
          </div>
        </div>

        <div className="rounded-3xl border bg-white p-8 flex flex-col">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-1">Libere este módulo</h2>
          <p className="text-sm text-slate-500 mb-6">Faça upgrade e comece a usar hoje. Sem fidelidade, cancele quando quiser.</p>
          <div className="space-y-3 flex-1">
            {plans.map((p, i) => (
              <button type="button" key={p.id} onClick={() => setSelected(p.id)} className={`w-full text-left rounded-2xl border p-4 transition-colors ${(melhor?.id === p.id) ? 'border-amber-400 bg-amber-50/60 ring-2 ring-amber-200' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`module-locked-plan-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{p.name}{i === 0 && <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-500 text-white px-1.5 py-0.5 rounded">recomendado</span>}</div>
                    <div className="text-xs text-slate-500">{PLAN_MODULES.filter((m) => planHasModule(p, m)).map((m) => m.label.split(' ')[0]).join(' • ')}</div>
                  </div>
                  <div className="text-right"><div className="text-xl font-bold text-slate-900">{formatCurrency(p.price)}</div><div className="text-xs text-slate-500">/mês</div></div>
                </div>
              </button>
            ))}
            {plans.length === 0 && <p className="text-sm text-slate-500">Carregando planos...</p>}
          </div>
          <div className="mt-6 space-y-2">
            <Button className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-base" disabled={!melhor} onClick={() => setPayPlan(melhor)} data-testid="module-locked-upgrade">
              <CreditCard className="w-4 h-4 mr-2" /> Assinar {melhor ? `${melhor.name} — ${formatCurrency(melhor.price)}/mês` : ''} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button type="button" onClick={() => navigate('/subscription')} className="w-full text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline py-1" data-testid="module-locked-subscription-link">Ver todos os detalhes da assinatura</button>
            <Button variant="outline" className="w-full" onClick={() => window.open(`https://wa.me/5511947289661?text=${encodeURIComponent(`Olá! Quero liberar o módulo ${mod.label} no meu plano.`)}`, '_blank')} data-testid="module-locked-support">
              <MessageCircle className="w-4 h-4 mr-2" /> Falar com o suporte
            </Button>
          </div>
        </div>
      </div>
      <PaymentModal isOpen={!!payPlan} onClose={() => setPayPlan(null)} plan={payPlan} onPaymentSuccess={onPaid} />
    </div>
  );
}
